import React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useFieldArray, useForm } from 'react-hook-form';
import { CURRENCY_USD } from 'constants/currency';
import { VAIR_MINIMUM_PLAYERS } from 'constants/vair';
import { SportsEnum } from 'types/generated/client';
import { getEventExperience } from 'utils/shared/events/getEventExperience';
import { OrderTotal } from 'utils/shared/money/calculateEventOrderTotal';
import { convertUnitPriceToFormattedPrice } from 'utils/shared/money/convertUnitPriceToFormattedPrice';
import { pluralize } from 'utils/shared/pluralize';
import { useViewer } from 'hooks/useViewer';
import { ButtonText } from 'components/Button';
import { Button } from 'components/Button/Button';
import { useModal } from 'components/modals/Modal';
import classNames from 'styles/utils/classNames';
import Header from './Header';
import ModalVairInfo from './ModalVairInfo';
import ReferralQuestion from './ReferralQuestion';
import SkillLevelQuestion from './SkillLevelQuestion';
import AddEventFormField from './UserEventList/AddEventFormField';
import {
  GroupsFormData,
  PropsSetRegistrationFormData,
  RegisterProps,
  RegistrationFormData,
  Steps,
  groupsFormSchema,
} from './types';

interface Props extends RegisterProps, PropsSetRegistrationFormData {
  isVairRoundRobin: boolean;
  invitationGroupId?: string | null;
  invitationTeamMembers?: { groupId: string; name: string; id: string }[] | null;
  isIgnoreInvitation?: boolean;
  setIsIgnoreInvitation: React.Dispatch<React.SetStateAction<boolean>>;
  orderTotal: OrderTotal;
  isUserOrganizer?: boolean;
  isNextButtonLoading?: boolean;
  onGameDayClick?: () => void;
  registrationFormData: RegistrationFormData;
}

export default function AddEvents({
  setSteps,
  event,
  handleNext,
  setRegistrationFormData,
  invitationGroupId,
  invitationTeamMembers,
  isIgnoreInvitation,
  setIsIgnoreInvitation,
  orderTotal,
  isUserOrganizer,
  isVairRoundRobin,
  isNextButtonLoading,
  onGameDayClick,
  registrationFormData,
}: Props) {
  const {
    isOpen: isVairInfoModalOpen,
    openModal: openVairInfoModal,
    closeModal: closeVairInfoModal,
  } = useModal();

  // Pre-select event: prioritize invitationGroupId (from team invitation), then check for single event
  const defaultGroupId = invitationGroupId || (event.groups.length === 1 ? event.groups[0].id : '');

  const experience = getEventExperience(event);
  const { hasPartners, hasCompetition } = experience;
  // Racket Social asks two intake questions of its own.
  const isSocialEvent = experience.kind === 'social';

  // Use saved registration data when returning from a later step (e.g. PartnerSetup → back)
  const hasSavedGroups = registrationFormData.groups.some((g) => g.groupId);
  const initialGroups = hasSavedGroups
    ? registrationFormData.groups
    : [{ groupId: defaultGroupId, hasPartner: hasPartners, partnerEmail: '', partnerName: '' }];

  const { control, formState, watch, handleSubmit, setValue } = useForm<GroupsFormData>({
    resolver: zodResolver(groupsFormSchema),
    defaultValues: {
      groups: initialGroups,
    },
  });
  const { isAnonymousSession, userId } = useViewer();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'groups',
  });

  const registeredPlayerCount = event?.registrationsAggregate?.aggregate?.count || 0;

  const handleGameDayClick = () => {
    if (isUserOrganizer && isVairRoundRobin && registeredPlayerCount < VAIR_MINIMUM_PLAYERS) {
      openVairInfoModal();
      return;
    }
    onGameDayClick?.();
  };

  const watchedGroups = watch('groups');
  const isGroupsLimit = watchedGroups.length >= event.groups.length;
  const totalPrice =
    orderTotal.subtotal +
    watchedGroups.reduce((acc: number, group: { groupId: string }) => {
      const matchingGroup = event.groups.find((g) => g.id === group.groupId);
      return acc + (matchingGroup?.priceUnitAmount || 0);
    }, 0);
  const groupedIdsUsed = watchedGroups.map((group: { groupId: string }) => group.groupId);
  const selectionKey = groupedIdsUsed.join('|');

  /**
   * Auto-select a row's event whenever only one is available to it. `defaultValues`
   * is read once at mount, so on its own it misses the cases that matter: groups
   * arriving after mount, a row added via "+ Add event" with a single group left,
   * and a pick in another row narrowing this one down to one.
   */
  React.useEffect(() => {
    groupedIdsUsed.forEach((groupId, index) => {
      if (groupId) return;

      const takenElsewhere = groupedIdsUsed.filter((id, i) => i !== index && !!id);
      const available = event.groups.filter((group) => !takenElsewhere.includes(group.id));

      if (available.length === 1) {
        setValue(`groups.${index}.groupId`, available[0].id);
      }
    });
  }, [selectionKey, event.groups, setValue]);

  const isPrivateWithOutCost = event?.isQuickCreate ? true : false;

  const handleFormSubmit = (data: GroupsFormData) => {
    let groupsToPass: GroupsFormData['groups'];
    // For VAIR round robin (single group), ensure the group is included even if not in form
    if (isVairRoundRobin && event.groups.length === 1) {
      const vairGroup = event.groups[0];
      groupsToPass = data.groups.map((group) => ({
        ...group,
        groupId: group.groupId || vairGroup.id,
      }));
      setRegistrationFormData((prev) => ({ ...prev, groups: groupsToPass }));
    } else {
      groupsToPass = data.groups;
      setRegistrationFormData((prev) => ({ ...prev, groups: data.groups }));
    }

    const wantsPartnerSetup = groupsToPass.some((g) => g.hasPartner === true);

    if (wantsPartnerSetup) {
      if (!userId) {
        setSteps(Steps.CreateAccount);
      } else {
        setSteps(Steps.PartnerSetup);
      }
      return;
    }

    handleNext(groupsToPass);
  };

  return (
    <form
      onSubmit={handleSubmit(handleFormSubmit)}
      className="tournament-register-form flex h-full flex-col items-start"
    >
      <Header
        title="Register"
        cta={
          experience.isPack
            ? 'Pack selection'
            : hasPartners
            ? 'Event and partner selection'
            : 'Event selection'
        }
        className="px-6 pt-6"
      />
      <div className="flex h-full w-full flex-auto flex-col overflow-y-auto px-6 pt-ds-xl">
        <div>
          {!isPrivateWithOutCost && (
            <div className="flex justify-between">
              <h2 className="typography-product-body-highlight text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                Registration fee
              </h2>
              <p className="typography-product-body-highlight text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                {convertUnitPriceToFormattedPrice(orderTotal.subtotal, CURRENCY_USD).priceDisplay}
              </p>
            </div>
          )}
          {fields.map((field, index) => (
            <AddEventFormField
              key={field.id}
              field={field}
              index={index}
              control={control}
              formErrors={formState.errors}
              selectedGroupId={watchedGroups[index]?.groupId}
              event={event}
              groupedIdsUsed={groupedIdsUsed}
              existingEventCount={0}
              watchedGroupsLength={watchedGroups.length}
              remove={remove}
              invitationTeamMembers={invitationTeamMembers}
              isIgnoreInvitation={isIgnoreInvitation}
              setIsIgnoreInvitation={setIsIgnoreInvitation}
              isVairRoundRobin={isVairRoundRobin}
              isPrivateWithOutCost={isPrivateWithOutCost}
              eventDisplayNumber={index + 1}
              hasPartner={watchedGroups[index]?.hasPartner ?? hasPartners}
              onHasPartnerChange={(value) => {
                setValue(`groups.${index}.hasPartner`, value);
              }}
              containerClassName={classNames(
                'flex flex-col gap-4',
                convertUnitPriceToFormattedPrice(orderTotal.registrationFee, CURRENCY_USD)
                  .priceFormatted > 0
                  ? 'mt-6'
                  : 'mt-0',
              )}
            />
          ))}
          {!isGroupsLimit && (
            <div className="mt-ds-xl flex items-start">
              <ButtonText
                onClick={() => {
                  append({
                    groupId: '',
                    hasPartner: hasPartners,
                    partnerEmail: '',
                    partnerName: '',
                  });
                }}
                className="typography-product-button-label-medium text-color-text-brand dark:text-color-text-brand"
              >
                + Add event
              </ButtonText>
            </div>
          )}
          {/*
            Skill level sits with what you're signing up for, above the divider — it
            feeds who you play with, not what you pay. Only pickleball and tennis have
            a skill level column on `users`.
          */}
          {isSocialEvent &&
            (event.sport === SportsEnum.Pickleball || event.sport === SportsEnum.Tennis) && (
              <SkillLevelQuestion
                value={registrationFormData.skillLevelId ?? ''}
                onChange={(value) =>
                  setRegistrationFormData((prev) => ({ ...prev, skillLevelId: value }))
                }
              />
            )}
        </div>
        <div className="my-ds-xl h-px w-full bg-color-border-input-lightmode dark:bg-color-border-input-darkmode" />
        {!isPrivateWithOutCost && (
          <div className="flex justify-between">
            <p className="typography-product-body-highlight text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
              Total
            </p>
            <p className="typography-product-body-highlight text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
              {convertUnitPriceToFormattedPrice(totalPrice, CURRENCY_USD).priceDisplay}
            </p>
          </div>
        )}
        {isSocialEvent && (
          <ReferralQuestion
            value={registrationFormData.referredBy ?? ''}
            onChange={(value) =>
              setRegistrationFormData((prev) => ({ ...prev, referredBy: value }))
            }
          />
        )}
      </div>
      <div
        className={classNames(
          'mt-ds-xl w-full px-6 pb-6',
          isUserOrganizer && 'flex flex-col gap-6',
          !isAnonymousSession && 'pb-6',
        )}
      >
        <Button
          type="submit"
          variant={isUserOrganizer ? 'secondary' : 'brand'}
          size="lg"
          loading={isNextButtonLoading}
        >
          Next
        </Button>
        {isUserOrganizer && hasCompetition && (
          <>
            <div className="border-t" />
            <Button variant="brand" size="lg" onClick={handleGameDayClick}>
              Gameday
            </Button>
          </>
        )}
      </div>

      {isAnonymousSession && (
        <div className="typography-product-caption mt-6 w-full px-6 pb-6 text-center text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
          Already registered?{' '}
          <ButtonText
            className="font-medium text-color-brand-primary"
            onClick={() => setSteps(Steps.Login)}
          >
            Login
          </ButtonText>
        </div>
      )}
      <ModalVairInfo
        isOpen={isVairInfoModalOpen}
        onClose={closeVairInfoModal}
        title="Minimum Players Required"
        message={`${VAIR_MINIMUM_PLAYERS} players are required before starting Gameday. Currently there ${pluralize(
          {
            count: registeredPlayerCount,
            singular: 'is',
            plural: 'are',
            hideCount: true,
          },
        )} ${pluralize({
          count: registeredPlayerCount,
          singular: 'player',
          plural: 'players',
        })} registered.`}
      />
    </form>
  );
}
